// The lawyer's copy certification with the Ordem, run by the pt-certificate
// service through the lawyer backend. A preview fills the act in and stops on
// the register's confirmation page; a registration registers exactly what the
// lawyer approved from that preview. Each call takes about a minute, and the
// service writes its steps to the case meanwhile (passport_certification_run).
//
// A child's case has three documents, so three acts: the child's own (the
// applicant's, like an adult's) and each parent's, told apart by `person`.

import { errorMessageFrom, requestBlob } from './client';

/** One step the service has reached, as written to the case. */
export interface CertificationStep {
  key: string;
  text: string;
  detail?: string | null;
  at?: string;
}

/** The run in progress or last finished, overwritten by each new run. */
export interface CertificationRun {
  mode: 'preview' | 'register';
  state: 'running' | 'done';
  started_at?: string;
  finished_at?: string;
  status?: string;
  reason?: string | null;
  steps: CertificationStep[];
}

/** The outcome on the case: a registered act, or why the last run stopped. */
export interface PassportCertificationRecord {
  status: 'registered' | 'registering' | 'failed' | 'needs_review' | 'refused' | 'approval_outdated' | string;
  registration_number?: string;
  consultation_code?: string;
  registered_at?: string;
  adopted?: boolean;
  resumo?: string;
  gcs_path?: string;
  error?: string;
  reason?: string;
  document?: { number?: string; holder?: string };
}

/** What the lawyer approves, exactly as the preview returned it. */
export interface CertificationApproval {
  number: string;
  description: string;
  sheets: number;
}

/** The service's answer. `status` is always there; `reason` when it stopped. */
export interface CertificationOutcome extends Partial<CertificationApproval> {
  status: string;
  reason?: string | null;
  resumo?: string;
  /** The register already holds an act for this document. */
  adopted?: boolean;
  registration_number?: string;
}

/** Whose document an act certifies: the applicant (an adult, or the child), or a child's parent. */
export type CertifiedPerson = 'applicant' | 'guardian_1' | 'guardian_2';

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;

const certificationPath = (caseId: string, action: string, person: CertifiedPerson = 'applicant') =>
  `/cases/${encodeURIComponent(caseId)}/passport-certification/${action}` +
  (person === 'applicant' ? '' : `?person=${person}`);

/** One act of a batch, as preview-all and register-all return it. */
export interface CertificationBatchAct extends CertificationOutcome {
  person: CertifiedPerson;
  /** Registered before this batch: neither previewed nor registered again. */
  already?: boolean;
  consultation_code?: string;
}

/** A batch's answer: the first problem's status (or preview / registered), and every act. */
export interface CertificationBatch extends CertificationOutcome {
  acts?: CertificationBatchAct[];
}

export type CertificationActApproval = CertificationApproval & { person: CertifiedPerson };

const post = async (token: string, path: string, body?: object): Promise<CertificationBatch> => {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    return { status: 'failed', reason: 'The portal could not reach the backend. Check the connection and try again.' };
  }
  const data = await response.json().catch(() => null);
  if (data && typeof data.status === 'string') return data as CertificationOutcome;
  // A refusal from the backend itself (403, 404, 422) carries `detail`.
  return {
    status: response.status === 403 ? 'forbidden' : 'failed',
    reason: errorMessageFrom(data, `The certification answered ${response.status}.`),
  };
};

export const previewPassportCertification = (token: string, caseId: string, person?: CertifiedPerson) =>
  post(token, certificationPath(caseId, 'preview', person));

export const registerPassportCertification = (
  token: string,
  caseId: string,
  approval: CertificationApproval,
  person?: CertifiedPerson
) => post(token, certificationPath(caseId, 'register', person), approval);

/** Every act a child's case still needs, previewed in order behind one sign-in. */
export const previewAllPassportCertifications = (token: string, caseId: string) =>
  post(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/preview-all`);

/** Registers the previewed acts in order, each only as approved; stops at the first that does not register. */
export const registerAllPassportCertifications = (token: string, caseId: string, acts: CertificationActApproval[]) =>
  post(token, `/cases/${encodeURIComponent(caseId)}/passport-certification/register-all`, { acts });

export const fetchPassportCertificationPdf = (token: string, caseId: string, person?: CertifiedPerson): Promise<Blob> =>
  requestBlob(token, certificationPath(caseId, 'pdf', person), 'Could not load the act');

export const fetchPassportCertificationThumbnail = (
  token: string,
  caseId: string,
  person?: CertifiedPerson
): Promise<Blob> => requestBlob(token, certificationPath(caseId, 'thumbnail', person), 'No preview');
